import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  getDueAlerts, getDemoAlerts, logEvent, setDismissed, setSnoozed,
  occurrenceKey, SNOOZE_OPTIONS, RELATED_MODULES,
} from "@/lib/alertsData";

const AlertsContext = createContext(null);
export const useAlerts = () => useContext(AlertsContext);

const DEMO_FLAG = "aesthetic-os-alerts-demo-shown";
const MAX_VISIBLE = 4;

export function AlertsProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const seenRef = useRef(new Set()); // rule ids currently queued (dedupe)

  const enqueue = useCallback((rule, { demo = false } = {}) => {
    const occ = occurrenceKey(rule);
    const qid = `${rule.id}-${occ}-${demo ? "demo" : "auto"}`;
    setQueue((q) => {
      if (q.some((a) => a.rule.id === rule.id)) return q;
      logEvent({ alert_rule_id: rule.id, user_id: user?.id, action: "shown" });
      return [...q, { qid, rule, occ }].slice(-MAX_VISIBLE);
    });
  }, [user]);

  const evaluate = useCallback(() => {
    if (!user || user.role === "client_user") return;
    getDueAlerts(user).forEach((rule) => {
      if (!seenRef.current.has(rule.id)) { seenRef.current.add(rule.id); enqueue(rule); }
    });
  }, [user, enqueue]);

  // Initial: demo alerts (once per session) + due evaluation
  useEffect(() => {
    if (!user || user.role === "client_user") return;
    const timer = setTimeout(() => {
      if (!sessionStorage.getItem(DEMO_FLAG)) {
        sessionStorage.setItem(DEMO_FLAG, "1");
        getDemoAlerts().forEach((rule, i) => setTimeout(() => enqueue(rule, { demo: true }), i * 600));
      }
      evaluate();
    }, 900);
    const interval = setInterval(evaluate, 60000);
    return () => { clearTimeout(timer); clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const remove = useCallback((qid) => setQueue((q) => q.filter((a) => a.qid !== qid)), []);

  const dismiss = useCallback((item) => {
    setDismissed(item.rule.id, user?.id, item.occ);
    logEvent({ alert_rule_id: item.rule.id, user_id: user?.id, action: "dismissed" });
    remove(item.qid);
  }, [user, remove]);

  const snooze = useCallback((item, optKey) => {
    const opt = SNOOZE_OPTIONS[optKey];
    const until = new Date(Date.now() + (opt?.ms || 3600000)).toISOString();
    setSnoozed(item.rule.id, user?.id, until);
    logEvent({ alert_rule_id: item.rule.id, user_id: user?.id, action: "snoozed", snoozed_until: until });
    seenRef.current.delete(item.rule.id);
    remove(item.qid);
  }, [user, remove]);

  const open = useCallback((item) => {
    const mod = RELATED_MODULES[item.rule.related_module || item.rule.button_action];
    logEvent({ alert_rule_id: item.rule.id, user_id: user?.id, action: "opened", opened_module: item.rule.related_module });
    remove(item.qid);
    if (mod?.path) navigate(mod.path);
  }, [user, remove, navigate]);

  // Preview from settings — shows immediately regardless of schedule/state
  const preview = useCallback((rule) => {
    const qid = `${rule.id}-preview-${Date.now()}`;
    logEvent({ alert_rule_id: rule.id, user_id: user?.id, action: "shown" });
    setQueue((q) => [...q, { qid, rule, occ: occurrenceKey(rule), preview: true }].slice(-MAX_VISIBLE));
  }, [user]);

  return (
    <AlertsContext.Provider value={{ queue, dismiss, snooze, open, preview }}>
      {children}
    </AlertsContext.Provider>
  );
}
